<script setup>
import {computed, ref, watch} from "vue";
import {useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
import axios from "@/axios";

const languageStore = useLanguageStore();
const props = defineProps(['insertVideoModal', 'liveData']);
const emits = defineEmits(['set_insertVideoModal', 'getLivePage']);

const liveId = ref();
const openYn = ref();
const meetingId = ref();
const meetingTitle = ref();
const meetingType = ref();
const meetingDescription = ref();
const videoFolder = ref();
const videoFileName = ref();

// 폴더 + 파일명으로 fullPath 자동 생성
const videoPath = computed(() => {
  const folder = videoFolder.value?.replace(/\/+$/, '') || '';
  const name = videoFileName.value || '';
  if (!folder && !name) return '';
  if (!folder) return name;
  if (!name) return folder;
  return folder + '/' + name;
})

const fn_close = () => {
  liveId.value = null;
  openYn.value = null;
  meetingId.value = null;
  meetingTitle.value = null;
  meetingDescription.value = null;
  meetingType.value = null;
  videoFolder.value = null;
  videoFileName.value = null;

  emits("set_insertVideoModal", false);
}
const fn_connect = async () => {
  if (!videoFolder.value) {
    toast.warning(languageStore.get('pleaseEnterVideoPath'));
    return;
  }
  if (!videoFileName.value) {
    toast.warning(languageStore.get('pleaseEnterVideoFileName'));
    return;
  }
  const data = {
    "meetingId": meetingId.value,
    "privateStatus": openYn.value ? "Y" : "N",
    "videoTitle": meetingTitle.value,
    "videoDescription": meetingDescription.value,
    "meetingType": meetingType.value,
    "videoPath": videoPath.value,
    "videoName": videoFileName.value
  }
  axios.post("/video/connectLive/" + liveId.value, data, {headers: {'Content-Type': 'application/json'}})
      .then(s => {
        emits('getLivePage');
        fn_close();
      }).catch(e => {
        toast.error(e.response?.data?.message || languageStore.get('videoConnectFailed'));
      })
}

watch(() => props.liveData, (newVal) => {
  if (newVal != null) {
    liveId.value = newVal.liveId;
    meetingId.value = newVal?.meeting?.meetingId;
    meetingTitle.value = newVal.liveTitle;
    meetingDescription.value = newVal.liveDescription;
    meetingType.value = newVal?.meeting?.meetingType?.codeId;
  }
}, {immediate: true})
</script>

<template>
  <v-dialog
      @update:model-value="fn_close()"
      :model-value="insertVideoModal"
      width="auto"
  >
    <v-card width="600">
      <v-card-title>{{ languageStore.get('videoConnect') }}</v-card-title>
      <v-card-subtitle>{{ languageStore.get('videoConnectSubtitle') }}</v-card-subtitle>
      <v-card-text>
        <v-text-field
            v-model="meetingTitle"
            :label="languageStore.get('requiredMeetingTitle')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="mb-4"
        ></v-text-field>

        <v-text-field
            v-model="videoFolder"
            :label="languageStore.get('videoFolderPath')"
            :placeholder="'/home/bestian/cms_jar/video'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="mb-4"
        >
          <template #prepend-inner>
            <v-icon size="small">mdi-folder-outline</v-icon>
          </template>
        </v-text-field>

        <v-text-field
            v-model="videoFileName"
            :label="languageStore.get('videoFileName')"
            :placeholder="'sample.mp4'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="mb-4"
        >
          <template #prepend-inner>
            <v-icon size="small">mdi-file-video-outline</v-icon>
          </template>
        </v-text-field>

        <v-text-field
            :model-value="videoPath"
            :label="languageStore.get('fullPath')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="mb-4"
            readonly
            bg-color="#f5f5f5"
        ></v-text-field>

        <v-textarea
            v-model="meetingDescription"
            :label="languageStore.get('meetingDescription')"
            variant="outlined"
            density="compact"
            rows="3"
            hide-details="auto"
            class="mb-4"
        ></v-textarea>

        <v-switch
            v-model="openYn"
            :label="languageStore.get('publicStatus')"
            color="primary"
            hide-details
            density="compact"
        ></v-switch>
      </v-card-text>

      <v-card-actions class="modal-actions">
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" @click="fn_connect">{{ languageStore.get('save') }}</v-btn>
        <v-btn variant="outlined" @click="fn_close">{{ languageStore.get('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 스타일 */
:deep(.v-card-title) {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px !important;
  font-weight: 600 !important;
  font-size: 18px !important;
}

:deep(.v-card-subtitle) {
  padding: 8px 24px !important;
  background-color: #1A3A5C !important;
  color: rgba(255, 255, 255, 0.8) !important;
  opacity: 1 !important;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text) {
  padding: 24px !important;
}

/* 모달 액션 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-actions) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

/* v-card-text 내 입력 필드 간격 */
.v-card-text :deep(.v-input) {
  margin-bottom: 16px;
}

.v-card-text :deep(.v-input:last-child),
.v-card-text :deep(.v-switch) {
  margin-bottom: 0;
}
</style>